/*
 * Tehtävän hoitaja (236, T534; pääChatin toimeksianto Tonin välittämänä 12.10.2026, Tonin idea).
 *
 * Kuusi työnjohtajaa samalla jonolla: rivi kertoo kuka sitä hoitaa. «Otan hoitaakseni» kirjaa itsen, työnjohtaja tai
 * pääkäyttäjä osoittaa toiselle (viesti osoitetulle) ja vapauttaa toisen hoitaman perusteella. Valmis on rivin oma
 * sulkeminen (puute ratkaistu, punch kuitattu) — hoito ei tarvitse omaa tilaa. Sama komponentti puutteelle ja punchille.
 */
import { useState } from "react";
import { api, ApiError, type Hoitaja as HoitajaTieto, type Kayttaja } from "./api";
import { t, virheTeksti } from "./kieli";
import { naytaPvm } from "./pvm";

export const saaOsoittaa = (k: Kayttaja) => k.roolit.some((r) => r === "paakayttaja" || r === "tyonjohtaja");

export function Hoitaja({ taulu, id, hoitaja, kayttaja, saaKirjoittaa, muuttui, inline = false, tyhjaTeksti = false }: {
  taulu: "puute" | "punch"; id: number; hoitaja: HoitajaTieto | null | undefined; kayttaja: Kayttaja;
  /** Saako kirjautunut kirjoittaa jonoon (sama ehto kuin rivin muut napit). */
  saaKirjoittaa: boolean; muuttui: () => void;
  /** Tekstin jatkona (linjan punch-lista: meta-rivi), ei omana lohkonaan. */
  inline?: boolean;
  /** false: tyhjästä tilasta ei tekstiä «Ei hoitajaa» — «Ota» kertoo sen (punch-selailun yhden napin sarake, 13.10.2026). */
  tyhjaTeksti?: boolean;
}) {
  const [virhe, setVirhe] = useState<string | null>(null);
  const [ehdokkaat, setEhdokkaat] = useState<{ id: number; nimi: string }[] | null>(null);
  const [valittu, setValittu] = useState<number | "">("");
  const [peruste, setPeruste] = useState<string | null>(null);
  const esimies = saaOsoittaa(kayttaja);
  const oma = hoitaja != null && hoitaja.id === kayttaja.id;

  const teko = (f: () => Promise<unknown>) => {
    setVirhe(null);
    f().then(() => { setEhdokkaat(null); setValittu(""); setPeruste(null); muuttui(); })
      .catch((e) => setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : String(e)));
  };
  const avaaOsoitus = () => {
    setVirhe(null);
    api.hoidonEhdokkaat(taulu, id).then((r) => setEhdokkaat(r.filter((x) => x.id !== hoitaja?.id)))
      .catch((e) => setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : String(e)));
  };

  // Toni 13.10.2026: «Ota» ja «Osoita…» pieninä toissijaisina nappeina samalla rivillä (tekstilinkkiä ei tunnistanut
  // painettavaksi); hoitajan nimi tekstinä + «Vapauta». Tila luetaan asettelusta: nimi = joku hoitaa, «Ota» = ei kukaan.
  // Osoitus- ja perustekenttä avautuvat vain pyydettäessä.
  const linkit: JSX.Element[] = [];
  if (saaKirjoittaa && ehdokkaat === null && peruste === null) {
    if (!hoitaja) linkit.push(<button key="ota" type="button" className="nappi nappi--kevyt nappi--pieni" data-hoito-ota=""
      onClick={() => teko(() => api.otaHoitoon(taulu, id))}>{t("hoito.ota")}</button>);
    if (oma) linkit.push(<button key="vap" type="button" className="nappi nappi--kevyt nappi--pieni" data-hoito-vapauta=""
      onClick={() => teko(() => api.vapautaHoidosta(taulu, id))}>{t("hoito.vapauta")}</button>);
    if (hoitaja && !oma && esimies) linkit.push(<button key="vapm" type="button" className="nappi nappi--kevyt nappi--pieni" data-hoito-vapauta=""
      onClick={() => setPeruste("")}>{t("hoito.vapauta")}…</button>);
    if (esimies) linkit.push(<button key="os" type="button" className="nappi nappi--kevyt nappi--pieni" data-hoito-osoita=""
      onClick={avaaOsoitus}>{t("hoito.osoita")}</button>);
  }
  const Juuri = inline ? "span" : "div";   // meta-rivin sisällä span, ei lohkoa
  return (
    <Juuri className={inline ? "hoitaja hoitaja--inline" : "hoitaja"} data-hoitaja={hoitaja?.nimi ?? ""} data-hoito-taulu={taulu} data-hoito-id={id}>
      {hoitaja ? (
        <span title={[naytaPvm(hoitaja.otettu.slice(0, 10)), hoitaja.osoittaja ? t("hoito.osoitti", { nimi: hoitaja.osoittaja }) : ""].filter(Boolean).join(" · ")}>
          {t("hoito.hoitaa", { nimi: hoitaja.nimi })}
        </span>
      ) : tyhjaTeksti ? <span className="dim">{t("hoito.ei_hoitajaa")}</span> : null}
      {linkit}
      {ehdokkaat !== null && (
        <span className="hoitaja__napit">
          {ehdokkaat.length === 0 ? <span className="dim">{t("hoito.ei_ehdokkaita")}</span> : (
            <select value={valittu} data-hoito-ehdokas="" aria-label={t("hoito.valitse")}
                    onChange={(e) => setValittu(e.target.value ? Number(e.target.value) : "")}>
              <option value="">{t("hoito.valitse")}</option>
              {ehdokkaat.map((x) => <option key={x.id} value={x.id}>{x.nimi}</option>)}
            </select>
          )}
          {ehdokkaat.length > 0 && <button type="button" className="nappi" data-hoito-osoita-nappi="" disabled={valittu === ""}
            onClick={() => valittu !== "" && teko(() => api.otaHoitoon(taulu, id, valittu))}>{t("hoito.osoita_nappi")}</button>}
          <button type="button" className="nappi nappi--kevyt" onClick={() => setEhdokkaat(null)}>{t("hoito.peruuta")}</button>
        </span>
      )}
      {peruste !== null && (
        <span className="hoitaja__napit">
          <input value={peruste} maxLength={500} data-hoito-peruste="" placeholder={t("hoito.peruste")}
                 aria-label={t("hoito.peruste")} onChange={(e) => setPeruste(e.target.value)} />
          <button type="button" className="nappi" disabled={!peruste.trim()} data-hoito-vapauta-nappi=""
            onClick={() => teko(() => api.vapautaHoidosta(taulu, id, peruste.trim()))}>{t("hoito.vapauta")}</button>
          <button type="button" className="nappi nappi--kevyt" onClick={() => setPeruste(null)}>{t("hoito.peruuta")}</button>
        </span>
      )}
      {virhe && <div className="huom huom--vika" role="alert">{virhe}</div>}
    </Juuri>
  );
}
